import { useEffect, useMemo, useRef } from "react";
import { Outlet, To, useNavigate } from "react-router";
import useUserAuthentication from "~/hooks/contexts/useUserAuthentication";
import useUserAuthenticationActions from "~/hooks/contexts/useUserAuthenticationActions";
import useToasts from "~/hooks/useToasts";

export type WithOrganizationParams = {
  isUserEmailChangeEnabled: boolean;
  redirectTo?: To;
};

export default function withOrganization(params: WithOrganizationParams) {
  function Wrapped() {
    const navigate = useNavigate();
    const { user } = useUserAuthentication();
    const { isNavigationInProgress } = useUserAuthenticationActions();
    const { addToast } = useToasts();
    const isRedirecting = useRef<boolean>(false);
    const isPermitted = useMemo<boolean | null>(() => {
      if (user === undefined) {
        return null;
      }

      if (user === null) {
        return false;
      }

      if (params.isUserEmailChangeEnabled) {
        return user.organization.isUserEmailChangeEnabled;
      }

      return true;
    }, [user]);

    useEffect(() => {
      if (!isNavigationInProgress && !isRedirecting.current && isPermitted === false) {
        isRedirecting.current = true;
        const to: To = params.redirectTo ?? "/settings";

        addToast({ body: "You are not authorized to see this page.", variant: "danger" });
        navigate(to, { replace: true });
      }
    }, [addToast, isNavigationInProgress, isPermitted, navigate]);

    return isPermitted === true ? (
      <Outlet />
    ) : (
      <div className="align-items-center bg-body bottom-0 d-flex end-0 flex-column justify-content-center p-4 position-fixed top-0 start-0">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return Wrapped;
}
